import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import {
    createRootRoute,
    createRoute,
    createRouter,
    redirect,
} from "@tanstack/react-router";

import { AppShell } from "./components/AppShell/AppShell";
import { TableSkeleton } from "./components/TableSkeleton";
import { AuthGate } from "./components/AuthGate";
import { featureFlags } from "./lib/featureFlags";
import { queryClient } from "./lib/queryClient";
import type { CurrentUser } from "./lib/auth";
import { ApprovalsPage } from "./routes/approvals";
import { AuditPage } from "./routes/audit";
import { CVEsPage } from "./routes/cves";
import { DashboardHome } from "./routes/index";
import { FeedsPage } from "./routes/feeds";
import { FeedDetailPage } from "./routes/feedDetail";
import { IoCsPage } from "./routes/iocs";
import { HeuristicPage } from "./routes/heuristic";
import { LicensesPage } from "./routes/licenses";
// PlaceholderPage reserved for future shell routes.
// import { PlaceholderPage } from "./routes/placeholder";
import { ProductsPage } from "./routes/products";
import { ProductDetailPage } from "./routes/productDetail";
import { RulesPage } from "./routes/rules";
import { RuleReviewQueuePage } from "./routes/ruleReviews";
import { RuleSettingsPage } from "./routes/ruleSettings";
import { SettingsPage } from "./routes/settings";
import { TenantsPage } from "./routes/tenants";
import { TenantDetailPage } from "./routes/tenantDetail";
import { UsersPage } from "./routes/users";

// Workflow routes are lazy-loaded — the React Flow editor is heavy (~150 kB
// gzipped) and only admin/operator users visit it. Keeping it out of the
// main bundle protects everyone else's first paint.
const WorkflowsPage = lazy(() =>
    import("./routes/workflows").then((m) => ({ default: m.WorkflowsPage })),
);
const WorkflowDetailPage = lazy(() =>
    import("./routes/workflowDetail").then((m) => ({ default: m.WorkflowDetailPage })),
);
const WorkflowReviewsPage = lazy(() =>
    import("./routes/workflowReviews").then((m) => ({ default: m.WorkflowReviewsPage })),
);
const WorkflowSettingsPage = lazy(() =>
    import("./routes/workflowSettings").then((m) => ({ default: m.WorkflowSettingsPage })),
);

// Phase 2.D — admin submissions queue. Lazy because the customer surface
// for the same data lives under /portal/submissions and we don't want to
// pull this into the customer chunk.
const SubmissionsAdminLazy = lazy(() =>
    import("./routes/submissions").then((m) => ({ default: m.SubmissionsAdminPage })),
);

// Import Wizard routes are lazy-loaded — the detail page pulls Monaco and
// the run drawer (~90 kB gz). admin/operator only.
const ImportsPage = lazy(() =>
    import("./routes/imports").then((m) => ({ default: m.ImportsPage })),
);
const ImportDetailPage = lazy(() =>
    import("./routes/importDetail").then((m) => ({ default: m.ImportDetailPage })),
);
const ImportTemplatesPage = lazy(() =>
    import("./routes/importTemplates").then((m) => ({ default: m.ImportTemplatesPage })),
);

// Phase 2.L — Detection rule registries (operator-managed catalog).
const RegistriesPageLazy = lazy(() =>
    import("./routes/registries").then((m) => ({ default: m.RegistriesPage })),
);

// YARA per-type routes — lazy so Monaco + YARA Monarch tokenizer stay in a
// separate chunk from the main bundle.
const YaraListPage = lazy(() =>
    import("./routes/yara/index").then((m) => ({ default: m.YaraListPage })),
);
const YaraDetailPage = lazy(() =>
    import("./routes/yara/detail").then((m) => ({ default: m.YaraDetailPage })),
);
const YaraNewPage = lazy(() =>
    import("./routes/yara/new").then((m) => ({ default: m.YaraNewPage })),
);
const YaraDistributionPage = lazy(() =>
    import("./routes/yara/distribution").then((m) => ({ default: m.YaraDistributionPage })),
);
// Phase 2.O REDUX — tier-threshold editor.
const YaraTierConfigPage = lazy(() =>
    import("./routes/yara/tier-config").then((m) => ({ default: m.YaraTierConfigPage })),
);
const SuricataDistributionPage = lazy(() =>
    import("./routes/suricata/distribution").then((m) => ({ default: m.SuricataDistributionPage })),
);
const ElasticDistributionPage = lazy(() =>
    import("./routes/elastic/distribution").then((m) => ({ default: m.ElasticDistributionPage })),
);

// Sigma per-type routes — lazy so Monaco + Sigma key overlay + Splunk SPL
// tokenizer + backend preview Tabs stay out of the main bundle.
const SigmaList = lazy(() =>
    import("./routes/sigma/index").then((m) => ({ default: m.SigmaListPage })),
);
const SigmaDetail = lazy(() =>
    import("./routes/sigma/detail").then((m) => ({ default: m.SigmaDetailPage })),
);
const SigmaNew = lazy(() =>
    import("./routes/sigma/new").then((m) => ({ default: m.SigmaNewPage })),
);

// Elastic per-type routes — lazy so Monaco + KQL/EQL/ES|QL tokenizers + the
// JSON-Schema validator stay in a separate chunk.
const ElasticList = lazy(() =>
    import("./routes/elastic/index").then((m) => ({ default: m.ElasticListPage })),
);
const ElasticDetail = lazy(() =>
    import("./routes/elastic/detail").then((m) => ({ default: m.ElasticDetailPage })),
);
const ElasticNew = lazy(() =>
    import("./routes/elastic/new").then((m) => ({ default: m.ElasticNewPage })),
);

// Custom per-type routes — lazy so Monaco JSON + schema dialogs ship in a
// separate chunk. Custom is the simplest type (no engine, no importer);
// admin-only schema CRUD lives at /rules/custom/schemas.
const CustomList = lazy(() =>
    import("./routes/custom/index").then((m) => ({ default: m.CustomListPage })),
);
const CustomDetail = lazy(() =>
    import("./routes/custom/detail").then((m) => ({ default: m.CustomDetailPage })),
);
const CustomNew = lazy(() =>
    import("./routes/custom/new").then((m) => ({ default: m.CustomNewPage })),
);
const CustomSchemas = lazy(() =>
    import("./routes/custom/schemas").then((m) => ({ default: m.CustomSchemasPage })),
);

// Phase 2.A — Customer portal routes. The PortalLayout + every page lives in
// its own chunk so customer users never download admin code (and vice-versa).
const PortalLayoutLazy = lazy(() => import("./layouts/PortalLayout"));
const PortalHomeLazy = lazy(() => import("./routes/portal/index"));
// Phase 2.C — real lookup + CVE drill replace the P2.A placeholder.
const PortalLookupLazy = lazy(() => import("./routes/portal/lookup"));
const PortalCveDetailLazy = lazy(() => import("./routes/portal/cveDetail"));
// Phase 2.C — real reports list + detail replace the P2.A placeholder.
const PortalReportsLazy = lazy(() => import("./routes/portal/reports"));
const PortalReportDetailLazy = lazy(() => import("./routes/portal/reportDetail"));
const PortalSubmissionsLazy = lazy(() => import("./routes/portal/submissions"));
const PortalAllowlistLazy = lazy(() => import("./routes/portal/allowlist"));
const PortalUsageLazy = lazy(() => import("./routes/portal/usage"));
// Phase 2.F — real portal Notifications pages replace the P2.A placeholder.
const PortalNotificationsLazy = lazy(() => import("./routes/portal/notifications"));
const PortalNotificationsEventsLazy = lazy(() =>
    import("./routes/portal/notifications-events"),
);
const PortalNotificationsDeliveriesLazy = lazy(() =>
    import("./routes/portal/notifications-deliveries"),
);
const PortalAccountLazy = lazy(() =>
    import("./routes/portal/placeholders").then((m) => ({ default: m.PortalAccount })),
);

// Phase 2.F — admin notifications routes. Lazy-loaded so the per-kind config
// form + drawers stay out of the main bundle for non-admin tiers.
const NotificationsChannelsPage = lazy(() => import("./routes/notifications/index"));
const NotificationsEventsPage = lazy(() => import("./routes/notifications/events"));
const NotificationsDeliveriesPage = lazy(() =>
    import("./routes/notifications/deliveries"),
);

// Phase 2.G — public-facing /docs tree. Lazy because react-markdown +
// rehype-highlight + the bundled markdown source add ~50 kB gz that
// non-developer users never need.
const DocsLandingLazy = lazy(() => import("./routes/docs/index"));
const DocsQuickstartLazy = lazy(() => import("./routes/docs/quickstart"));
const DocsErrorsLazy = lazy(() => import("./routes/docs/errors"));
const DocsSDKsLazy = lazy(() => import("./routes/docs/sdks"));
const DocsIntegrationLazy = lazy(() => import("./routes/docs/integration"));
const DocsContractLazy = lazy(() => import("./routes/docs/contract"));
// API Playground — interactive sandbox for the public threat-intel surface.
// Lazy because the catalog literal + the response viewer live in this chunk
// and only DMX/DSX integration developers need it.
const PlaygroundLazy = lazy(() => import("./routes/playground"));

// Suricata per-type routes — lazy so the Suricata Monarch tokenizer + header
// / body forms + SID-range badge ship in a separate chunk.
const SuricataList = lazy(() =>
    import("./routes/suricata/index").then((m) => ({ default: m.SuricataListPage })),
);
const SuricataDetail = lazy(() =>
    import("./routes/suricata/detail").then((m) => ({ default: m.SuricataDetailPage })),
);
const SuricataNew = lazy(() =>
    import("./routes/suricata/new").then((m) => ({ default: m.SuricataNewPage })),
);

function ListFallback(): React.ReactElement {
    return (
        <div className="stack-lg" style={{ padding: "var(--space-4)" }}>
            <TableSkeleton rows={6} columns={5} />
        </div>
    );
}
function DetailFallback(): React.ReactElement {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                padding: "var(--space-8)",
            }}
        >
            <Loader2 size={24} className="animate-spin" aria-label="Loading" />
        </div>
    );
}

// rootRoute holds AuthGate. AuthGate either renders LoginPage (no user) or
// the <Outlet />, which resolves to the appLayoutRoute below.
const rootRoute = createRootRoute({
    component: AuthGate,
});

// isCustomerOnly returns true when the user holds the customer role and no
// internal-staff role. Internal users that *also* happen to have customer
// (rare) keep the admin shell.
function isCustomerOnly(user: CurrentUser | null | undefined): boolean {
    if (!user) return false;
    const roles = user.roles ?? [];
    const hasCustomer = roles.includes("customer");
    const hasInternal =
        roles.includes("admin") ||
        roles.includes("operator") ||
        roles.includes("analyst");
    return hasCustomer && !hasInternal;
}

// appLayoutRoute is a pathless layout route: provides AppShell (rail + top
// bar + cmd bar + main outlet) for every authenticated route. Customer-only
// users are redirected to /portal at load time so they never see admin nav.
const appLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "app",
    component: AppShell,
    beforeLoad: ({ location }) => {
        if (!featureFlags.portalEnabled) return;
        const user = queryClient.getQueryData<CurrentUser | null>(["auth", "me"]);
        if (!isCustomerOnly(user)) return;
        // /docs/* is a public-within-the-app surface — both admins and
        // customers can browse it. Don't bounce customer-only users
        // away from the developer documentation.
        if (location.pathname.startsWith("/docs")) return;
        throw redirect({ to: "/portal" });
    },
});

// portalLayoutRoute mounts the customer-only PortalLayout at /portal. Admins
// can browse here for preview (a banner says so); customers cannot leave.
const portalLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "portal-layout",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalLayoutLazy />
        </Suspense>
    ),
});
const portalIndexRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <PortalHomeLazy />
        </Suspense>
    ),
});
const portalLookupRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/lookup",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalLookupLazy />
        </Suspense>
    ),
});
const portalCveDetailRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/cves/$cveId",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalCveDetailLazy />
        </Suspense>
    ),
});
const portalReportsRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/reports",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalReportsLazy />
        </Suspense>
    ),
});
const portalReportDetailRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/reports/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalReportDetailLazy />
        </Suspense>
    ),
});
const portalSubmissionsRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/submissions",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalSubmissionsLazy />
        </Suspense>
    ),
});
const portalAllowlistRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/allowlist",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalAllowlistLazy />
        </Suspense>
    ),
});
const portalUsageRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/usage",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalUsageLazy />
        </Suspense>
    ),
});
const portalNotificationsRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/notifications",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalNotificationsLazy />
        </Suspense>
    ),
});
const portalNotificationsEventsRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/notifications/events",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalNotificationsEventsLazy />
        </Suspense>
    ),
});
const portalNotificationsDeliveriesRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/notifications/deliveries",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalNotificationsDeliveriesLazy />
        </Suspense>
    ),
});
const portalAccountRoute = createRoute({
    getParentRoute: () => portalLayoutRoute,
    path: "/portal/account",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PortalAccountLazy />
        </Suspense>
    ),
});

const indexRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/",
    component: DashboardHome,
});


const iocsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/iocs",
    component: IoCsPage,
});
const heuristicRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/heuristic",
    component: HeuristicPage,
});
const cvesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/cves",
    component: CVEsPage,
});
const rulesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules",
    component: RulesPage,
});
const ruleReviewsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/reviews",
    component: RuleReviewQueuePage,
});
const registriesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/registries",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <RegistriesPageLazy />
        </Suspense>
    ),
});
const ruleSettingsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/settings",
    component: RuleSettingsPage,
});
// ruleDetailRoute removed in Wave 1 — per-type detail pages (e.g. /rules/yara/$id)
// land in Wave 2.
// YARA routes. /rules/yara/new is declared BEFORE /rules/yara/$id so the
// param route doesn't swallow the literal "new" segment.
const yaraListRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/yara",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <YaraListPage />
        </Suspense>
    ),
});
const yaraNewRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/yara/new",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <YaraNewPage />
        </Suspense>
    ),
});
// /rules/yara/distribution declared before /rules/yara/$id so the literal
// segment isn't swallowed by the param route.
const yaraDistributionRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/yara/distribution",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <YaraDistributionPage />
        </Suspense>
    ),
});
// Phase 2.O REDUX — tier-threshold editor. Declared alongside distribution
// so both literal routes resolve before the $id param route below.
const yaraTierConfigRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/yara/tier-config",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <YaraTierConfigPage />
        </Suspense>
    ),
});
const yaraDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/yara/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <YaraDetailPage />
        </Suspense>
    ),
});

// Sigma routes — `/new` declared before `/r/$id` so the param route doesn't
// swallow the literal "new" segment. Detail uses /r/ to disambiguate from
// list and base /rules/{id}/versions per the existing yara convention.
const sigmaListRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/sigma",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <SigmaList />
        </Suspense>
    ),
});
const sigmaNewRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/sigma/new",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <SigmaNew />
        </Suspense>
    ),
});
const sigmaDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/sigma/r/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <SigmaDetail />
        </Suspense>
    ),
});

// Elastic routes — `/new` declared before `/r/$id` so the param route
// doesn't swallow the literal "new" segment. Detail uses /r/ to
// disambiguate per the existing yara/sigma convention.
const elasticListRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/elastic",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <ElasticList />
        </Suspense>
    ),
});
const elasticNewRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/elastic/new",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <ElasticNew />
        </Suspense>
    ),
});
const elasticDistributionRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/elastic/distribution",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <ElasticDistributionPage />
        </Suspense>
    ),
});
const elasticDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/elastic/r/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <ElasticDetail />
        </Suspense>
    ),
});

// Suricata routes — `/new` declared before `/r/$id` so the param route doesn't
// swallow the literal "new" segment. Detail uses /r/ to disambiguate per the
// existing yara/sigma convention (and matching the backend route shape).
const suricataListRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/suricata",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <SuricataList />
        </Suspense>
    ),
});
const suricataNewRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/suricata/new",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <SuricataNew />
        </Suspense>
    ),
});
const suricataDistributionRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/suricata/distribution",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <SuricataDistributionPage />
        </Suspense>
    ),
});
const suricataDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/suricata/r/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <SuricataDetail />
        </Suspense>
    ),
});

// Custom routes — `/new` and `/schemas` declared before `/r/$id` so the
// param route doesn't swallow the literal segments. Per-rule detail uses
// `/r/` to disambiguate per the existing yara/sigma/suricata convention.
const customListRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/custom",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <CustomList />
        </Suspense>
    ),
});
const customNewRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/custom/new",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <CustomNew />
        </Suspense>
    ),
});
const customSchemasRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/custom/schemas",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <CustomSchemas />
        </Suspense>
    ),
});
const customDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/rules/custom/r/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <CustomDetail />
        </Suspense>
    ),
});

const workflowsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/workflows",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <WorkflowsPage />
        </Suspense>
    ),
});
const workflowReviewsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/workflows/reviews",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <WorkflowReviewsPage />
        </Suspense>
    ),
});
const workflowSettingsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/workflows/settings",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <WorkflowSettingsPage />
        </Suspense>
    ),
});
const workflowDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/workflows/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <WorkflowDetailPage />
        </Suspense>
    ),
});
const feedsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/feeds",
    component: FeedsPage,
});
const importsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/imports",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <ImportsPage />
        </Suspense>
    ),
});
const importTemplatesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/imports/templates",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <ImportTemplatesPage />
        </Suspense>
    ),
});
const importDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/imports/$id",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <ImportDetailPage />
        </Suspense>
    ),
});
const feedDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/feeds/$slug",
    component: FeedDetailPage,
});
const tenantsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/tenants",
    component: TenantsPage,
});
const tenantDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/tenants/$tenantId",
    component: TenantDetailPage,
});
const licensesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/licenses",
    component: LicensesPage,
});
const productsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/products",
    component: ProductsPage,
});
const productDetailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/products/$slug",
    component: ProductDetailPage,
});
const usersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/users",
    component: UsersPage,
});
const settingsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/settings",
    component: SettingsPage,
});
const auditRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/audit",
    component: AuditPage,
});
const approvalsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/approvals",
    component: ApprovalsPage,
});
const submissionsAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/submissions",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <SubmissionsAdminLazy />
        </Suspense>
    ),
});

// Phase 2.G — public docs tree. Mounted under appLayoutRoute so
// admins see it inside the standard rail; customer-only users are
// allowed through the appLayoutRoute redirect when the path begins
// with /docs (see appLayoutRoute.beforeLoad above).
const docsLandingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <DocsLandingLazy />
        </Suspense>
    ),
});
const docsQuickstartRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs/quickstart",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <DocsQuickstartLazy />
        </Suspense>
    ),
});
const docsErrorsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs/errors",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <DocsErrorsLazy />
        </Suspense>
    ),
});
const docsSDKsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs/sdks",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <DocsSDKsLazy />
        </Suspense>
    ),
});
const docsIntegrationRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs/integration",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <DocsIntegrationLazy />
        </Suspense>
    ),
});
const docsContractRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs/contract",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <DocsContractLazy />
        </Suspense>
    ),
});
const playgroundRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/docs/playground",
    component: () => (
        <Suspense fallback={<DetailFallback />}>
            <PlaygroundLazy />
        </Suspense>
    ),
});

// Phase 2.F — admin notifications routes.
const notificationsChannelsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/notifications",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <NotificationsChannelsPage />
        </Suspense>
    ),
});
const notificationsEventsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/notifications/events",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <NotificationsEventsPage />
        </Suspense>
    ),
});
const notificationsDeliveriesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: "/notifications/deliveries",
    component: () => (
        <Suspense fallback={<ListFallback />}>
            <NotificationsDeliveriesPage />
        </Suspense>
    ),
});

const routeTree = rootRoute.addChildren([
    portalLayoutRoute.addChildren([
        portalIndexRoute,
        portalLookupRoute,
        portalCveDetailRoute,
        portalReportsRoute,
        portalReportDetailRoute,
        portalSubmissionsRoute,
        portalAllowlistRoute,
        portalUsageRoute,
        portalNotificationsRoute,
        portalNotificationsEventsRoute,
        portalNotificationsDeliveriesRoute,
        portalAccountRoute,
    ]),
    appLayoutRoute.addChildren([
        indexRoute,
        iocsRoute,
        heuristicRoute,
        cvesRoute,
        rulesRoute,
        ruleReviewsRoute,
        ruleSettingsRoute,
        registriesRoute,
        yaraListRoute,
        yaraNewRoute,
        yaraDistributionRoute,
        yaraTierConfigRoute,
        yaraDetailRoute,
        sigmaListRoute,
        sigmaNewRoute,
        sigmaDetailRoute,
        elasticListRoute,
        elasticNewRoute,
        elasticDistributionRoute,
        elasticDetailRoute,
        customListRoute,
        customNewRoute,
        customSchemasRoute,
        customDetailRoute,
        suricataListRoute,
        suricataNewRoute,
        suricataDistributionRoute,
        suricataDetailRoute,
        workflowsRoute,
        workflowReviewsRoute,
        workflowSettingsRoute,
        workflowDetailRoute,
        feedsRoute,
        feedDetailRoute,
        importsRoute,
        importTemplatesRoute,
        importDetailRoute,
        tenantsRoute,
        tenantDetailRoute,
        licensesRoute,
        productsRoute,
        productDetailRoute,
        usersRoute,
        settingsRoute,
        auditRoute,
        approvalsRoute,
        submissionsAdminRoute,
        notificationsChannelsRoute,
        notificationsEventsRoute,
        notificationsDeliveriesRoute,
        docsLandingRoute,
        docsQuickstartRoute,
        docsErrorsRoute,
        docsSDKsRoute,
        docsIntegrationRoute,
        docsContractRoute,
        playgroundRoute,
    ]),
]);

export const router = createRouter({
    routeTree,
    defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}
